import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

export const THEMES = [
  {
    title: "Economic Analysis & Market Sizing",
    short: "Market Sizing",
    blurb: "Demand estimates and revenue pools that hold up in front of a CFO.",
    subs: [
      {
        name: "Top-down & bottom-up sizing",
        detail: "Aggregate anchors versus pipeline-built floors, reconciled.",
        articles: [
          { title: "The Ceiling Is Not the Opportunity", type: "Essay", brief: "Why aggregate market anchors describe limits, not openings — and how to read a syndicated number without mistaking it for your demand." },
          { title: "Building Demand Models from Procurement Pipelines", type: "Paper", brief: "A bottom-up build from tenders, unit economics and delivery capacity, with the failure modes to check before the model is trusted." },
        ],
      },
      {
        name: "Triangulation (TDT)",
        detail: "Cross-examining estimates until the stories agree.",
        articles: [
          { title: "Three Witnesses, One Number: The TDT Discipline", type: "Paper", brief: "The working method for triangulating top-down, bottom-up and benchmark views into an estimate you can defend line by line." },
          { title: "When Top-Down and Bottom-Up Disagree", type: "Essay", brief: "What the gap between the two estimates is actually telling you — and how to close it without averaging away the insight." },
        ],
      },
      {
        name: "Revenue pools & opportunity sizing",
        detail: "Where value concentrates — and when to enter.",
        articles: [
          { title: "Mapping Revenue Pools Before You Chase Them", type: "Essay", brief: "A pool-first approach to opportunity: where value concentrates, how it migrates, and which segments justify entry." },
          { title: "Entry Windows: Timing Demand, Not Hype", type: "Paper", brief: "On reading adoption curves and procurement cycles so entry is sequenced with evidence rather than enthusiasm." },
        ],
      },
      {
        name: "Benchmarking & expert inputs",
        detail: "Secondary data disciplined by primary voices.",
        articles: [
          { title: "What Experts Know That Datasets Don't", type: "Essay", brief: "How to structure expert input so it disciplines a model instead of decorating it — questions, weights and sanity checks." },
          { title: "Benchmarking Without Cargo Cults", type: "Essay", brief: "Why borrowed benchmarks mislead, and how to rebuild comparators around your market's actual economics." },
        ],
      },
    ],
  },
  {
    title: "Urban & Infrastructure Planning",
    short: "Urban & Infra",
    blurb: "Data-driven frameworks for cities, regions and the assets inside them.",
    subs: [
      {
        name: "Demographic & spatial datasets",
        detail: "Population, economics and land-use fused into one view.",
        articles: [
          { title: "Cities Are Numbers with a Skyline", type: "Essay", brief: "Reading a city through its datasets before its postcards: what population, wages and land-use reveal that visits don't." },
          { title: "Fusing Census, Economy and Land-Use into One Map", type: "Paper", brief: "A practical method for layering demographic, economic and spatial data into a single planning evidence base." },
        ],
      },
      {
        name: "Masterplans & business cases",
        detail: "From evidence to investable programmes.",
        articles: [
          { title: "From Masterplan to Money: The Business Case Bridge", type: "Paper", brief: "How masterplans become investable: revenue logic, phasing and the assumptions a finance committee will test." },
          { title: "Why Masterplans Fail at the Financing Page", type: "Essay", brief: "The recurring gap between beautiful spatial plans and fundable programmes — and how to close it early." },
        ],
      },
      {
        name: "Infrastructure gap assessments",
        detail: "Reusable diagnostics for cities and regions.",
        articles: [
          { title: "The Infrastructure Gap, Measured Honestly", type: "Paper", brief: "A diagnostic for quantifying what a city lacks — services, systems and capacity — in terms a budget can act on." },
          { title: "A Scorecard Cities Actually Use", type: "Essay", brief: "Designing assessment tools that administrations pick up and reuse, rather than file away." },
        ],
      },
      {
        name: "Asset & portfolio optimisation",
        detail: "Lifecycle, utilisation and scenario analysis.",
        articles: [
          { title: "Lifecycle Thinking for Land and Buildings", type: "Paper", brief: "What changes when assets are evaluated across their full life — acquisition to exit — instead of a single year." },
          { title: "Utilisation Is the Quiet KPI", type: "Essay", brief: "How usage data exposes underperforming portfolios, and the scenarios that turn idle space into strategy." },
        ],
      },
    ],
  },
  {
    title: "Sustainability & Energy Transition",
    short: "Sustainability",
    blurb: "Transition economics turned into business cases that survive scrutiny.",
    subs: [
      {
        name: "Transition business cases",
        detail: "Honest ranges over optimistic point estimates.",
        articles: [
          { title: "Why Energy Transition Business Cases Fail", type: "Essay", brief: "The three assumptions — discount-rate optimism, frozen demand, unpriced policy risk — that quietly undo transition cases." },
          { title: "Pricing Policy Risk Without Paralysis", type: "Essay", brief: "A working approach to putting a number on regulatory uncertainty instead of pretending it is zero." },
        ],
      },
      {
        name: "Policy & regulatory analysis",
        detail: "Reading the rulebook before writing the roadmap.",
        articles: [
          { title: "Reading the Rulebook Before the Roadmap", type: "Essay", brief: "Why transition strategy starts with the regulatory calendar, and how to map obligations into investment timing." },
          { title: "Subsidies, Sunsets and Strategic Timing", type: "Paper", brief: "On incentive windows: when public money shifts the business case, and when it merely delays the reckoning." },
        ],
      },
      {
        name: "Implementation roadmaps",
        detail: "Phased execution with KPIs that hold.",
        articles: [
          { title: "Phasing Decarbonisation Without Breaking Operations", type: "Paper", brief: "Sequencing transition investment so service continuity, cash flow and targets stay compatible." },
          { title: "KPIs That Survive Year Two", type: "Essay", brief: "Choosing transition metrics that remain measurable, auditable and meaningful after the launch photos fade." },
        ],
      },
    ],
  },
  {
    title: "Digital Transformation & AI",
    short: "Digital & AI",
    blurb: "Readiness diagnostics and adoption strategy — with judgement kept human.",
    subs: [
      {
        name: "Digital readiness scorecards",
        detail: "Institutional, infrastructural and human capacity, measured.",
        articles: [
          { title: "Measuring a Municipality's Appetite for Change", type: "Paper", brief: "The three-axis scorecard — institutional, infrastructural, human — that predicts whether a digital masterplan will move." },
          { title: "The Three Axes of Digital Readiness", type: "Essay", brief: "Mandates, systems and skills: how to score each from evidence rather than sentiment." },
        ],
      },
      {
        name: "Gen AI adoption strategy",
        detail: "Augment the analyst, not the judgement.",
        articles: [
          { title: "Generative AI in the Consulting Stack", type: "Essay", brief: "Where Gen AI genuinely accelerates research work — and the points where it quietly degrades it." },
          { title: "Augment, Don't Replace: A Working Line for AI Teams", type: "Essay", brief: "Automation for assembly, augmentation for analysis, accountability with a human name attached." },
        ],
      },
      {
        name: "GCC strategy",
        detail: "Capability centres as engines of metropolitan economies.",
        articles: [
          { title: "The GCC Decade and the New Geography of Talent", type: "Paper", brief: "How capability centres moved from cost arbitrage to capability strategy, and what that means for cities." },
          { title: "Why Cities Compete for the Second GCC", type: "Essay", brief: "Footprint multipliers, talent ladders and the spillovers that decide whether a hub deepens or drifts." },
        ],
      },
    ],
  },
  {
    title: "Market & Competitive Intelligence",
    short: "Intelligence",
    blurb: "Five hundred reports of pattern recognition, distilled into decisions.",
    subs: [
      {
        name: "Competitor anatomy",
        detail: "Who wins, why they win, and what breaks them.",
        articles: [
          { title: "How Winners Actually Win", type: "Essay", brief: "Competitor anatomy beyond the league table: the mechanics of advantage, and where they are fragile." },
          { title: "Competitor Teardowns That End in Decisions", type: "Paper", brief: "A teardown format built to finish in a 'therefore', not a matrix." },
        ],
      },
      {
        name: "Industry research at scale",
        detail: "500+ reports of signal over ceremony.",
        articles: [
          { title: "Five Hundred Reports, One Insight", type: "Essay", brief: "What a decade of market intelligence teaches about framing: the question is the analysis." },
          { title: "The Discipline of Deleting", type: "Essay", brief: "Signal is what survives when every sentence a busy executive would skim is removed." },
        ],
      },
      {
        name: "Analyst positioning",
        detail: "Influencing the influencers, honestly.",
        articles: [
          { title: "Analyst Relations as a Growth Lever", type: "Essay", brief: "How analyst positioning shapes enterprise shortlists — and how to work the channel honestly." },
          { title: "Positioning Honestly in a Hype Market", type: "Essay", brief: "On differentiation that survives scrutiny: fewer adjectives, more evidence." },
        ],
      },
    ],
  },
  {
    title: "Strategic Consulting & Deal Advisory",
    short: "Advisory",
    blurb: "From problem statement to phased execution — recommendations that stick.",
    subs: [
      {
        name: "Strategic assessments",
        detail: "Diagnostics before prescriptions.",
        articles: [
          { title: "Diagnostics Before Prescriptions", type: "Essay", brief: "Why the best consulting starts with measurement, and what a good assessment must include to be acted on." },
          { title: "The Problem Statement Is the Strategy", type: "Essay", brief: "The one paragraph that decides whether an engagement creates value — and why it is most often skipped." },
        ],
      },
      {
        name: "IT transactions & deal advisory",
        detail: "Multi-million-dollar sourcing lifecycles, end to end.",
        articles: [
          { title: "Anatomy of a Multi-Million-Dollar Sourcing Deal", type: "Paper", brief: "Inside a large IT transaction: scoping, evaluation and the decision points where deals are won or lost." },
          { title: "What Enterprise Buyers Never Tell Providers", type: "Essay", brief: "Notes from the buy side of the table: how enterprises actually score, select and stall." },
        ],
      },
      {
        name: "GTM & sales strategy",
        detail: "Pipelines that convert — a 20% uplift, evidenced.",
        articles: [
          { title: "GTM Is a Research Problem First", type: "Essay", brief: "Why go-to-market plans built on evidence out-convert plans built on enthusiasm." },
          { title: "The 20% Conversion Uplift Playbook", type: "Paper", brief: "The pipeline mechanics behind a measured 20% lift: targeting, messaging and proof at each stage." },
        ],
      },
      {
        name: "C-suite advisory",
        detail: "Recommendations that survive the boardroom.",
        articles: [
          { title: "Recommendations That Survive the Boardroom", type: "Essay", brief: "What makes a recommendation defensible under challenge — structure, evidence and the courage to caveat." },
          { title: "Writing for the Executive Who Skims", type: "Essay", brief: "Headline-first writing for decision-makers: how to make one page carry the whole argument." },
        ],
      },
    ],
  },
];

const pos = (i, total, r) => {
  const a = (i / total) * 2 * Math.PI - Math.PI / 2;
  return { left: `${50 + r * Math.cos(a)}%`, top: `${50 + r * Math.sin(a)}%` };
};

function ArticleRows({ articles, tidPrefix, t, s }) {
  return (
    <div className="mt-2.5 space-y-1.5">
      {articles.map((a, k) => (
        <Link
          key={k}
          to={`/atlas/${t}/${s}/${k}`}
          data-testid={`${tidPrefix}-${k}`}
          className="group flex items-baseline gap-3"
        >
          <span className="shrink-0 border border-blue/30 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.15em] text-blue transition-colors group-hover:border-blue group-hover:bg-blue group-hover:text-white">
            {a.type}
          </span>
          <span className="font-serif text-[13px] italic leading-snug text-navy-soft underline decoration-transparent underline-offset-2 transition-colors group-hover:text-blue group-hover:decoration-blue">
            {a.title}
          </span>
        </Link>
      ))}
    </div>
  );
}

export default function ResearchWheel() {
  const [active, setActive] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const theme = active === null ? null : THEMES[active];

  useEffect(() => {
    if (active !== null && selectedSub !== null) {
      document
        .getElementById(`atlas-sub-${active}-${selectedSub}`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [active, selectedSub]);

  const pick = (i) => {
    setActive((prev) => (prev === i ? null : i));
    setSelectedSub(null);
  };

  return (
    <section id="research-atlas" className="wrap py-24 lg:py-32" data-testid="research-atlas-section">
      <div className="mb-14 max-w-2xl">
        <p className="overline-mono">The research atlas</p>
        <h2 className="font-serif text-3xl leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
          Six themes. Open one.
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-navy-soft sm:text-base">
          Click a circle to expand a research theme into its sub-themes — each with its articles, papers
          and the detail behind the work.
        </p>
      </div>

      <div className="hidden gap-12 lg:grid lg:grid-cols-12">
        <div className="self-start lg:col-span-7">
          <div className="relative mx-auto aspect-square w-full max-w-[620px]" data-testid="research-wheel">
            <div className="absolute inset-[10%] rounded-full border border-line" aria-hidden="true" />
            <div className="absolute inset-[30%] rounded-full border border-line" aria-hidden="true" />

            <button
              onClick={() => { setActive(null); setSelectedSub(null); }}
              data-testid="research-wheel-hub"
              aria-label="Close theme"
              className="absolute left-1/2 top-1/2 flex h-28 w-28 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-navy bg-navy text-white transition-transform duration-300 hover:scale-105"
            >
              <span className="font-serif text-lg italic">AER</span>
              <span className="mt-1 text-[8px] font-semibold uppercase tracking-[0.25em] text-white/60">Atlas</span>
            </button>

            {THEMES.map((t, i) => {
              const p = pos(i, THEMES.length, 40);
              const isActive = active === i;
              return (
                <div key={t.title} className="absolute -translate-x-1/2 -translate-y-1/2" style={p}>
                  <button
                    data-testid={`research-wheel-theme-${i}`}
                    onClick={() => pick(i)}
                    className="group flex flex-col items-center"
                  >
                    <span
                      className={`flex h-16 w-16 items-center justify-center rounded-full border font-serif text-lg transition-all duration-300 group-hover:scale-110 ${
                        isActive ? "border-blue bg-blue text-white" : "border-line-strong bg-white text-navy group-hover:border-blue"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={`mt-2 max-w-[110px] text-center text-[9px] font-semibold uppercase leading-tight tracking-[0.12em] transition-colors ${
                        isActive ? "text-blue" : "text-navy-soft"
                      }`}
                    >
                      {t.short}
                    </span>
                  </button>

                  <AnimatePresence>
                    {isActive &&
                      t.subs.map((s, j) => {
                        const base = (i / THEMES.length) * 2 * Math.PI - Math.PI / 2;
                        const fan = base + Math.PI + (j - (t.subs.length - 1) / 2) * 0.45;
                        const sx = Math.cos(fan) * 104;
                        const sy = Math.sin(fan) * 104;
                        const isSel = selectedSub === j;
                        return (
                          <motion.button
                            key={s.name}
                            initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
                            animate={{ opacity: 1, scale: 1, x: sx, y: sy }}
                            exit={{ opacity: 0, scale: 0, x: 0, y: 0 }}
                            transition={{ type: "spring", stiffness: 260, damping: 22, delay: j * 0.05 }}
                            onClick={(e) => { e.stopPropagation(); setSelectedSub(isSel ? null : j); }}
                            className={`absolute left-1/2 top-8 z-10 -translate-x-1/2 whitespace-nowrap border px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.12em] shadow-sm transition-colors duration-200 ${
                              isSel
                                ? "border-blue bg-blue text-white"
                                : "border-blue/40 bg-white text-blue hover:border-blue hover:bg-blue hover:text-white"
                            }`}
                            data-testid={`research-wheel-sub-${i}-${j}`}
                          >
                            {s.name}
                          </motion.button>
                        );
                      })}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-5">
          <AnimatePresence mode="wait">
            {theme ? (
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="border border-line bg-paper-band p-8"
                data-testid="research-wheel-panel"
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-blue">
                  Theme {String(active + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-3 font-serif text-2xl text-navy">{theme.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-navy-soft">{theme.blurb}</p>
                <div className="mt-6 border-t border-line">
                  {theme.subs.map((s, j) => (
                    <div
                      key={s.name}
                      id={`atlas-sub-${active}-${j}`}
                      data-testid={`research-wheel-panel-sub-${active}-${j}`}
                      className={`border-b border-line py-4 transition-all duration-300 ${
                        selectedSub === j ? "-mx-3 border-l-2 border-l-blue bg-white px-3" : ""
                      }`}
                    >
                      <p className="text-sm font-semibold text-navy">{s.name}</p>
                      <p className="mt-1 text-sm leading-relaxed text-navy-soft">{s.detail}</p>
                      <ArticleRows articles={s.articles} tidPrefix={`research-wheel-article-${active}-${j}`} t={active} s={j} />
                    </div>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="idle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex h-full min-h-[320px] flex-col items-center justify-center border border-dashed border-line-strong p-10 text-center"
                data-testid="research-wheel-panel-idle"
              >
                <p className="font-serif text-2xl italic text-navy-mute">Select a theme</p>
                <p className="mt-3 max-w-xs text-sm text-navy-mute">
                  Each circle opens into its sub-themes — with the articles and papers behind the work.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="lg:hidden">
        {THEMES.map((t, i) => {
          const isActive = active === i;
          return (
            <div key={t.title} className="border-t border-line">
              <button
                data-testid={`research-wheel-mobile-theme-${i}`}
                onClick={() => pick(i)}
                className="flex w-full items-center justify-between gap-4 py-5 text-left"
              >
                <span className="flex items-baseline gap-4">
                  <span className={`text-[10px] font-semibold tracking-[0.2em] ${isActive ? "text-blue" : "text-navy-mute"}`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif text-lg text-navy">{t.title}</span>
                </span>
                <Plus
                  size={16}
                  className={`shrink-0 transition-transform duration-300 ${isActive ? "rotate-45 text-blue" : "text-navy-mute"}`}
                />
              </button>
              <AnimatePresence initial={false}>
                {isActive && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <p className="pb-4 pl-9 text-sm leading-relaxed text-navy-soft">{t.blurb}</p>
                    {t.subs.map((s, j) => (
                      <div key={s.name} className="ml-9 border-l-2 border-blue/30 pb-5 pl-4">
                        <p className="text-sm font-semibold text-navy">{s.name}</p>
                        <p className="mt-1 text-sm leading-relaxed text-navy-soft">{s.detail}</p>
                        <ArticleRows articles={s.articles} tidPrefix={`research-wheel-mobile-article-${i}-${j}`} t={i} s={j} />
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
        <div className="border-t border-line" />
      </div>
    </section>
  );
}